import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Topbar from '../components/Topbar'
import { useToast } from '../context/ToastContext'
import { useTranslation } from '../i18n/LanguageContext'
import { getCurrencySymbol } from '../utils/currencies'
import POSCart     from '../components/pos/POSCart'
import POSProducts from '../components/pos/POSProducts'
import POSOptions  from '../components/pos/POSOptions'
import POSModals   from '../components/pos/POSModals'
import {
  MODE_PAYMENT_METHODS, POS_MODES,
} from '../components/pos/pos.constants'

// ── Helpers ────────────────────────────────────────────────────
function blankCustomer() {
  return { name:'', phone:'', email:'', address:'', age:'', gender:'', bin:'', lookedUp:false, id:null, storeCredit:0 }
}
function blankOptions(seller) {
  return {
    discountEnabled: false, discountPercent: '',
    taxEnabled:      false, taxPercent: seller?.pos_tax_percent || '', taxType: seller?.pos_tax_name || 'VAT',
    paymentMethod:   'Cash', paymentMethod2: '', amountPaid: '', amountPaid2: '',
    splitEnabled:    false,
    branchName:      seller?.pos_default_branch || '', staffName: '', note: '',
    // Jewellery extras
    oldGoldEnabled:  false, oldGoldWeight: '', oldGoldKarat: '22K', oldGoldValue: 0,
    // Service/repair extras
    jobCardNumber: '', technicianName: '', advancePaid: '', estimatedReady: '',
    // Food extras
    tableNumber: '', coverCount: '',
  }
}

export default function POS() {
  const { seller, isFeatureAllowed } = useAuth()
  const navigate     = useNavigate()
  const { showToast } = useToast()
  const { t }        = useTranslation()
  const sym          = getCurrencySymbol(seller?.seller_currency || 'BDT')
  const posMode      = seller?.pos_mode || 'standard'
  const isJewellery  = posMode === 'jewellery'

  // ── Refs ──────────────────────────────────────────────────────
  const barcodeRef = useRef(null)
  const videoRef   = useRef(null)
  const streamRef  = useRef(null)
  const rafRef     = useRef(null)

  // ── Core state — ALL hooks unconditionally before any return ──
  const [staffSession, setStaffSession]   = useState(null)  // { id, name, role, branch }
  const [showStaffLogin, setShowStaffLogin] = useState(false)
  const [products, setProducts]           = useState([])
  const [categories, setCategories]       = useState([])
  const [activeCategory, setActiveCategory] = useState('All')
  const [goldRates, setGoldRates]         = useState(null)  // today's gold rates
  const [cart, setCart]                   = useState([])
  const [customer, setCustomer]           = useState(blankCustomer())
  const [options, setOptions]             = useState(() => blankOptions(seller))
  const [heldOrders, setHeldOrders]       = useState([])
  const [todayStats, setTodayStats]       = useState({ sales:0, orders:0, returns:0, voids:0 })
  const [loading, setLoading]             = useState(true)
  const [selling, setSelling]             = useState(false)
  const [lookingUp, setLookingUp]         = useState(false)

  // Search/scan
  const [searchQuery, setSearchQuery]     = useState('')
  const [cameraOpen, setCameraOpen]       = useState(false)
  const [cameraError, setCameraError]     = useState('')
  const [scanning, setScanning]           = useState(false)
  const [scanFlash, setScanFlash]         = useState(false)

  // Modal state — all in one object to avoid too many useState calls
  const [modal, setModal] = useState({
    type: null,     // 'numpad' | 'variant' | 'receipt' | 'return' | 'void' |
                    //  'shift' | 'gold_rate' | 'custom_item' | 'partial' | 'hold_label'
    data: null,
  })
  const openModal  = (type, data = null) => setModal({ type, data })
  const closeModal = () => setModal({ type: null, data: null })

  // Numpad target (which cart item qty / price is being edited)
  const [numpadTarget, setNumpadTarget]   = useState(null) // { cartId, field: 'qty'|'price' }
  const [numpadValue, setNumpadValue]     = useState('')
  const [variantPicker, setVariantPicker] = useState(null)
  const [receiptOrder, setReceiptOrder]   = useState(null)

  // Variant picker
  const [pickerQty, setPickerQty]         = useState(1)
  const [pickerNote, setPickerNote]       = useState('')
  const [pickerWeight, setPickerWeight]   = useState('')  // for jewellery/weight modes

  // ── Derived calculations ──────────────────────────────────────
  const subtotal       = cart.reduce((s, i) => s + i.unit_price * i.quantity, 0)
  const discountAmount = options.discountEnabled && options.discountPercent
    ? Math.round(subtotal * Number(options.discountPercent) / 100) : 0
  const afterDiscount  = subtotal - discountAmount
  const taxAmount      = options.taxEnabled && options.taxPercent
    ? Math.round(afterDiscount * Number(options.taxPercent) / 100) : 0
  const oldGoldDeduction = isJewellery && options.oldGoldEnabled ? Number(options.oldGoldValue) || 0 : 0
  const storeCreditUsed  = options.paymentMethod === 'Store Credit'
    ? Math.min(customer.storeCredit, afterDiscount + taxAmount) : 0
  const total          = Math.max(0, afterDiscount + taxAmount - oldGoldDeduction - storeCreditUsed)
  const amountPaid     = Number(options.amountPaid) || 0
  const amountPaid2    = Number(options.amountPaid2) || 0
  const totalPaid      = options.splitEnabled ? amountPaid + amountPaid2 : amountPaid
  const change         = options.paymentMethod === 'Cash' && !options.splitEnabled
    ? Math.max(0, amountPaid - total) : 0
  const balanceDue     = Math.max(0, total - totalPaid)

  // Filtered products for the grid
  const filteredProducts = products.filter(p => {
    const matchCat = activeCategory === 'All' || p.category === activeCategory ||
                    (activeCategory === 'Pinned' && p.is_pinned)
    const q = searchQuery.toLowerCase()
    const matchSearch = !q || p.name?.toLowerCase().includes(q) ||
                        p.product_code?.toLowerCase().includes(q)
    return matchCat && matchSearch
  })

  // Available payment methods for this mode
  const availablePayments = MODE_PAYMENT_METHODS[posMode] || MODE_PAYMENT_METHODS.default

  // ── Data fetching ─────────────────────────────────────────────
  useEffect(() => {
    if (seller?.id) {
      fetchProducts()
      fetchTodayStats()
      fetchHeldOrders()
      if (isJewellery) fetchGoldRates()
      if (seller.pos_require_pin) setShowStaffLogin(true)
    }
  }, [seller]) // eslint-disable-line

  // Keep options in sync when seller loads
  useEffect(() => {
    if (seller) setOptions(blankOptions(seller))
  }, [seller?.id]) // eslint-disable-line

  const fetchProducts = async () => {
    setLoading(true)
    const { data } = await supabase.from('products')
      .select('id, name, product_code, sell_price, stock, buy_price, is_pinned, color, image_url, category, unit_type, unit_price_per, gold_karat, gold_weight, making_charge, making_charge_type, hallmark_number, stone_value, is_service, service_duration_min, requires_prescription, medicine_generic, expiry_date, batch_number, product_mode')
      .eq('seller_id', seller.id).eq('status', 'active')
      .order('is_pinned', { ascending: false }).order('name')
    const prods = data || []
    setProducts(prods)
    // Auto-generate category tabs
    const cats = ['All', 'Pinned', ...new Set(prods.map(p => p.category).filter(Boolean))].filter((c,i,a) => a.indexOf(c) === i)
    setCategories(cats)
    setLoading(false)
  }

  const fetchTodayStats = async () => {
    const start = new Date(); start.setHours(0, 0, 0, 0)
    const { data } = await supabase.from('orders')
      .select('total_amount, is_return, voided_at')
      .eq('seller_id', seller.id).eq('order_source', 'Walk-in / POS')
      .gte('created_at', start.toISOString())
    if (data) {
      setTodayStats({
        sales:   data.filter(o => !o.is_return && !o.voided_at).reduce((s, o) => s + Number(o.total_amount || 0), 0),
        orders:  data.filter(o => !o.is_return && !o.voided_at).length,
        returns: data.filter(o => o.is_return).length,
        voids:   data.filter(o => o.voided_at).length,
      })
    }
  }

  const fetchHeldOrders = async () => {
    const { data } = await supabase.from('pos_held_orders')
      .select('*').eq('seller_id', seller.id).order('updated_at', { ascending: false })
    setHeldOrders(data || [])
  }

  const fetchGoldRates = async () => {
    const today = new Date().toISOString().split('T')[0]
    const { data } = await supabase.from('pos_gold_rates')
      .select('*').eq('seller_id', seller.id).eq('effective_date', today).single()
    setGoldRates(data || null)
  }

  // ── Cart operations ───────────────────────────────────────────
  const addToCartDirect = useCallback((product, qty, variantName, note, weightOverride) => {
    const colorSuffix  = product.color ? ` (${product.color})` : ''
    const displayName  = variantName ? `${product.name}${colorSuffix} — ${variantName}` : `${product.name}${colorSuffix}`
    const variantData  = variantPicker?.variants?.find(v => v.variant_name === variantName)
    const basePrice    = variantData?.sell_price ? Number(variantData.sell_price) : Number(product.sell_price || 0)
    const stock        = variantData?.stock ?? product.stock
    const cartId       = variantName ? `${product.id}-${variantName}` : product.id

    // Jewellery / weight mode: price = weight × rate or per-unit
    let unitPrice = basePrice
    if (isJewellery && product.gold_karat && goldRates) {
      const rateKey = `rate_${product.gold_karat.toLowerCase().replace('k','').replace(' ','_')}k`
      const rate = goldRates[rateKey] || 0
      const weight = weightOverride || product.gold_weight || 0
      const goldValue = weight * rate
      let makingCharge = 0
      if (product.making_charge_type === 'per_gram') makingCharge = weight * (product.making_charge || 0)
      else if (product.making_charge_type === 'flat') makingCharge = product.making_charge || 0
      else if (product.making_charge_type === 'percent') makingCharge = goldValue * (product.making_charge || 0) / 100
      const stoneValue = product.stone_value || 0
      unitPrice = Math.round(goldValue + makingCharge + stoneValue)
    } else if (product.unit_price_per && weightOverride) {
      unitPrice = Math.round(basePrice * Number(weightOverride))
    }

    setCart(prev => {
      const existing = prev.find(i => i.cartId === cartId)
      if (existing && !isJewellery) {
        return prev.map(i => i.cartId === cartId ? { ...i, quantity: i.quantity + qty } : i)
      }
      return [...prev, {
        cartId, id: product.id, name: displayName,
        product_code: product.product_code, unit_price: unitPrice,
        quantity: qty, stock, note, variant_name: variantName || null,
        unit_type: product.unit_type || 'piece', weight: weightOverride || null,
        gold_karat: product.gold_karat || null, gold_weight: weightOverride || product.gold_weight || null,
        making_charge: product.making_charge || null, making_charge_type: product.making_charge_type || null,
        hallmark_number: product.hallmark_number || null, stone_value: product.stone_value || null,
        is_service: product.is_service || false,
      }]
    })
    setVariantPicker(null)
    setSearchQuery('')
    showToast(`${displayName} added`)
  }, [isJewellery, goldRates, variantPicker]) // eslint-disable-line

  const openVariantPicker = async (product) => {
    const { data: vars }  = await supabase.from('product_variants').select('*').eq('product_id', product.id).order('variant_name')
    const { data: prod2 } = await supabase.from('products').select('smart_fields, category').eq('id', product.id).single()
    const needsWeightInput = isJewellery && product.gold_karat
    if (vars && vars.length > 0) {
      setPickerQty(1); setPickerNote(''); setPickerWeight('')
      setVariantPicker({ product, variants: vars, smartFields: prod2?.smart_fields || {}, category: prod2?.category || '', needsWeightInput })
      openModal('variant')
    } else if (needsWeightInput) {
      setPickerQty(1); setPickerNote(''); setPickerWeight('')
      setVariantPicker({ product, variants: [], needsWeightInput: true })
      openModal('variant')
    } else {
      addToCartDirect(product, 1, '', '')
    }
  }

  const updateQty = (cartId, qty) => {
    if (qty <= 0) { setCart(p => p.filter(i => i.cartId !== cartId)); return }
    setCart(p => p.map(i => i.cartId === cartId ? { ...i, quantity: qty } : i))
  }
  const updatePrice   = (cartId, price) => setCart(p => p.map(i => i.cartId === cartId ? { ...i, unit_price: Number(price) } : i))
  const removeFromCart = (cartId) => setCart(p => p.filter(i => i.cartId !== cartId))

  const clearSale = useCallback(() => {
    setCart([]); setCustomer(blankCustomer()); setOptions(blankOptions(seller))
    setSearchQuery(''); setScanFlash(false)
    setTimeout(() => barcodeRef.current?.focus(), 100)
  }, [seller]) // eslint-disable-line

  // ── Hold / Park order ─────────────────────────────────────────
  const holdOrder = async (label) => {
    if (cart.length === 0) return
    const { data } = await supabase.from('pos_held_orders').insert({
      seller_id: seller.id, label: label || `Hold ${new Date().toLocaleTimeString('en-BD', { hour:'2-digit', minute:'2-digit' })}`,
      held_by: staffSession?.name || 'Seller',
      cart: JSON.stringify(cart), customer: JSON.stringify(customer),
      options: JSON.stringify(options), pos_mode: posMode,
    }).select().single()
    if (data) {
      showToast('Order held — ' + (label || 'unnamed'))
      await fetchHeldOrders()
      clearSale()
    }
  }

  const restoreHeld = async (held) => {
    try {
      setCart(JSON.parse(held.cart) || [])
      setCustomer(JSON.parse(held.customer) || blankCustomer())
      setOptions(JSON.parse(held.options) || blankOptions(seller))
    } catch {
      showToast('Could not restore held order', 'error'); return
    }
    await supabase.from('pos_held_orders').delete().eq('id', held.id)
    await fetchHeldOrders()
    showToast('Order restored: ' + held.label)
  }

  const deleteHeld = async (id) => {
    await supabase.from('pos_held_orders').delete().eq('id', id)
    await fetchHeldOrders()
  }

  // ── Customer lookup ───────────────────────────────────────────
  const handlePhoneLookup = async () => {
    if (!customer.phone || customer.phone.length < 7) return
    setLookingUp(true)
    const { data } = await supabase.from('customers').select('*')
      .eq('seller_id', seller.id)
      .or(`phone.eq.${customer.phone},phone.eq.+880${customer.phone}`)
      .limit(1).single()
    setLookingUp(false)
    if (data) {
      setCustomer(c => ({ ...c,
        name: data.name || '', email: data.email || '',
        address: [data.house, data.road, data.moholla, data.thana, data.district].filter(Boolean).join(', '),
        age: data.age || '', gender: data.gender || '', bin: data.bin_number || '',
        lookedUp: true, id: data.id, storeCredit: Number(data.store_credit || 0),
      }))
      showToast(`${t('pos.toast_customer_found')} ${data.name || 'Unknown'}${data.store_credit > 0 ? ` · Credit: ${sym}${data.store_credit}` : ''}`)
    } else {
      showToast(t('pos.toast_no_customer'), 'info')
      setCustomer(c => ({ ...c, lookedUp: false, id: null, storeCredit: 0 }))
    }
  }

  // ── Barcode / Camera ──────────────────────────────────────────
  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (streamRef.current) streamRef.current.getTracks().forEach(tk => tk.stop())
    streamRef.current = null; setCameraOpen(false); setScanning(false); setScanFlash(false)
  }

  const openCamera = async () => {
    setCameraError(''); setCameraOpen(true); setScanning(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode:'environment', width:{ ideal:1280 }, height:{ ideal:720 } } })
      streamRef.current = stream
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play() }
      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats:['code_128','code_39','ean_13','ean_8','qr_code','upc_a','upc_e'] })
        const tick = async () => {
          if (!streamRef.current) return
          try {
            const barcodes = await detector.detect(videoRef.current)
            if (barcodes.length > 0) {
              const code = barcodes[0].rawValue
              setScanFlash(true); setTimeout(() => setScanFlash(false), 300); stopCamera()
              const found = products.find(p => p.product_code?.toLowerCase() === code.toLowerCase())
              if (found) { openVariantPicker(found) }
              else { setSearchQuery(code); showToast(t('pos.toast_scanned')) }
              return
            }
          } catch {}
          rafRef.current = requestAnimationFrame(tick)
        }
        videoRef.current.addEventListener('playing', () => { rafRef.current = requestAnimationFrame(tick) }, { once:true })
      } else {
        setCameraError(t('pos.camera_fallback')); setScanning(false)
      }
    } catch {
      setCameraError(t('pos.camera_denied')); setCameraOpen(false); setScanning(false)
    }
  }

  const handleBarcodeSubmit = (e) => {
    e?.preventDefault()
    if (!searchQuery.trim()) return
    const found = products.find(p => p.product_code?.toLowerCase() === searchQuery.trim().toLowerCase())
    if (found) { openVariantPicker(found); setSearchQuery('') }
    else showToast(t('pos.toast_not_found'), 'error')
  }

  // ── Main sale ─────────────────────────────────────────────────
  const handleSell = async () => {
    if (cart.length === 0) return
    setSelling(true)
    const orderCode  = 'POS-' + Date.now().toString(36).toUpperCase()
    const nowISO     = new Date().toISOString()
    const primary    = cart[0]
    const otherItems = cart.slice(1)
    const notesText  = [
      otherItems.length > 0 ? 'Items: ' + otherItems.map(i => `${i.name} x${i.quantity}`).join(', ') : null,
      options.note || null,
      options.branchName ? 'Branch: ' + options.branchName : null,
      customer.bin ? 'Client BIN: ' + customer.bin : null,
      options.tableNumber ? 'Table: ' + options.tableNumber : null,
      options.jobCardNumber ? 'Job Card: ' + options.jobCardNumber : null,
    ].filter(Boolean).join(' | ') || null

    // Upsert customer
    let customerId = customer.id || null
    if (!customerId && (customer.name || customer.phone)) {
      if (customer.phone) {
        const { data: ex } = await supabase.from('customers').select('id').eq('seller_id', seller.id).eq('phone', customer.phone).limit(1).single()
        customerId = ex?.id || null
      }
      if (!customerId) {
        const { data: nc } = await supabase.from('customers').insert({
          seller_id: seller.id, name: customer.name || 'Walk-in',
          phone: customer.phone || null, email: customer.email || null,
          age: customer.age || null, gender: customer.gender || null,
          bin_number: customer.bin || null,
        }).select().single()
        customerId = nc?.id
      } else if (customer.name) {
        await supabase.from('customers').update({ name: customer.name, email: customer.email || undefined }).eq('id', customerId)
      }
    }

    // Deduct store credit if used
    if (storeCreditUsed > 0 && customerId) {
      await supabase.from('customers').update({ store_credit: Math.max(0, customer.storeCredit - storeCreditUsed) }).eq('id', customerId)
    }

    // Build order payload
    const orderPayload = {
      seller_id: seller.id, customer_id: customerId, order_code: orderCode,
      product_id: primary.id, product_code: primary.product_code, product_name: primary.name,
      quantity: primary.quantity, unit_price: primary.unit_price,
      delivery_charge: 0, total_amount: total,
      discount_percent: options.discountEnabled ? Number(options.discountPercent) : null,
      discount_amount: discountAmount || null,
      vat_percent: options.taxEnabled ? Number(options.taxPercent) : null,
      vat_amount: taxAmount || null, tax_type: options.taxEnabled ? options.taxType : null,
      payment_method: options.splitEnabled ? options.paymentMethod : options.paymentMethod,
      payment_method_2: options.splitEnabled ? options.paymentMethod2 : null,
      amount_paid: options.splitEnabled ? amountPaid : (amountPaid || total),
      amount_paid_2: options.splitEnabled ? amountPaid2 : null,
      balance_due: balanceDue || null,
      payment_status: balanceDue > 0 ? 'Partially paid' : 'Paid in full',
      store_credit_used: storeCreditUsed || null,
      order_source: 'Walk-in / POS',
      submitted_by: staffSession?.name || options.staffName || 'Seller',
      branch_name: options.branchName || null,
      status: 'closed', notes: notesText,
      pos_mode: posMode, created_at: nowISO,
      // Jewellery
      gold_rate_used: isJewellery ? goldRates?.[`rate_${primary.gold_karat?.toLowerCase().replace('k','').replace(' ','_')}k`] || null : null,
      gold_karat: primary.gold_karat || null, gold_weight: primary.gold_weight || null,
      making_charge: primary.making_charge || null, making_charge_type: primary.making_charge_type || null,
      stone_value: primary.stone_value || null, hallmark_number: primary.hallmark_number || null,
      old_gold_weight: isJewellery && options.oldGoldEnabled ? Number(options.oldGoldWeight) || null : null,
      old_gold_karat: isJewellery && options.oldGoldEnabled ? options.oldGoldKarat : null,
      old_gold_value: isJewellery && options.oldGoldEnabled ? oldGoldDeduction || null : null,
      // Service
      job_card_number: options.jobCardNumber || null,
      technician_name: staffSession?.name || options.technicianName || null,
      advance_paid: options.advancePaid ? Number(options.advancePaid) : null,
      estimated_ready: options.estimatedReady || null,
      // Food
      table_number: options.tableNumber || null,
      cover_count: options.coverCount ? Number(options.coverCount) : null,
    }

    const { data: newOrder, error } = await supabase.from('orders').insert(orderPayload).select().single()
    if (error) { showToast(t('pos.toast_sale_failed') + ' ' + error.message, 'error'); setSelling(false); return }

    // Deduct stock for physical products
    for (const item of cart) {
      if (item.is_service) continue
      const prod = products.find(p => p.id === item.id)
      if (prod) {
        await supabase.from('products').update({ stock: Math.max(0, (prod.stock || 0) - item.quantity) }).eq('id', item.id)
        await supabase.from('stock_log').insert({ product_id: item.id, action: 'deducted', quantity: item.quantity, note: `POS — ${orderCode}`, done_by: 'POS' })
      }
    }

    await fetchProducts(); await fetchTodayStats()
    setSelling(false)
    setReceiptOrder({
      ...newOrder, items: cart, customer_name: customer.name || null,
      staff_name: staffSession?.name || options.staffName || null,
      branch_name: options.branchName || null, customer_bin: customer.bin || null,
      created_at: nowISO, sym, subtotal, discountAmount, taxAmount,
      oldGoldDeduction, storeCreditUsed, total, change, balanceDue,
      paymentMethod: options.paymentMethod, paymentMethod2: options.paymentMethod2,
      amountPaid, amountPaid2, splitEnabled: options.splitEnabled,
    })
    openModal('receipt')
    clearSale()
  }

  // ── Feature gate ─────────────────────────────────────────────
  if (!isFeatureAllowed('pos')) {
    return (
      <div style={{ flex:1, background:'var(--bg-page)', fontFamily:'var(--font-ui)' }}>
        <Topbar title="Point of Sale" />
        <div style={{ padding:'24px', maxWidth:'480px', margin:'0 auto' }}>
          <div style={{ background:'var(--bg-card)', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)', padding:'32px', textAlign:'center', boxShadow:'var(--shadow-sm)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin:'0 auto 12px', display:'block' }}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <div style={{ fontSize:'16px', fontWeight:700, color:'var(--text-primary)', marginBottom:'8px' }}>{t('pos.upgrade_title')}</div>
            <div style={{ fontSize:'13px', color:'var(--text-muted)', marginBottom:'24px', lineHeight:1.7 }}>{t('pos.upgrade_desc')}</div>
            <button onClick={() => navigate('/pricing')}
              style={{ width:'100%', padding:'13px', borderRadius:'var(--radius-lg)', background:'var(--primary)', border:'none', fontSize:'14px', fontWeight:700, color:'#fff', cursor:'pointer' }}>
              {t('pos.upgrade_btn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Staff login screen ────────────────────────────────────────
  if (showStaffLogin && !staffSession) {
    return (
      <POSStaffLogin
        sellerId={seller.id}
        onLogin={(staff) => { setStaffSession(staff); setShowStaffLogin(false); setOptions(o => ({ ...o, staffName: staff.name, branchName: staff.branch || o.branchName })) }}
        onSkip={() => setShowStaffLogin(false)}
      />
    )
  }

  // ── Props bundle ──────────────────────────────────────────────
  const cartProps = {
    cart, subtotal, discountAmount, afterDiscount, taxAmount,
    oldGoldDeduction, storeCreditUsed, total, change, balanceDue,
    options, setOptions, customer,
    selling, availablePayments, isJewellery, sym, posMode,
    goldRates,
    onUpdateQty: updateQty, onUpdatePrice: updatePrice, onRemove: removeFromCart,
    onCharge: handleSell, onClearSale: clearSale,
    onHoldOrder: (label) => holdOrder(label),
    onOpenNumpad: (cartId, field) => { setNumpadTarget({ cartId, field }); setNumpadValue(''); openModal('numpad') },
    onOpenCustomItem: () => openModal('custom_item'),
    onOpenPartial: () => openModal('partial'),
    onOpenVoid: () => openModal('void'),
    onOpenReturn: () => openModal('return'),
    onOpenShiftClose: () => openModal('shift'),
    staffSession,
  }

  const productProps = {
    products: filteredProducts, allProducts: products,
    categories, activeCategory, setActiveCategory,
    searchQuery, setSearchQuery,
    cameraOpen, cameraError, scanning, scanFlash,
    videoRef, barcodeRef,
    loading, posMode, goldRates, sym, isJewellery,
    onAddProduct: openVariantPicker,
    onBarcodeSubmit: handleBarcodeSubmit,
    onCameraOpen: openCamera, onCameraStop: stopCamera,
  }

  const optionsProps = {
    customer, setCustomer, options, setOptions,
    lookingUp, onPhoneLookup: handlePhoneLookup,
    heldOrders, onRestoreHeld: restoreHeld, onDeleteHeld: deleteHeld,
    onHoldCurrent: () => openModal('hold_label'),
    goldRates, isJewellery, posMode, sym,
    onOpenGoldRate: () => openModal('gold_rate'),
    staffSession, seller,
  }

  const modalProps = {
    modal, closeModal,
    // Numpad
    numpadTarget, numpadValue, setNumpadValue,
    onNumpadConfirm: (val) => {
      if (!numpadTarget) return
      if (numpadTarget.field === 'qty') updateQty(numpadTarget.cartId, Number(val))
      else updatePrice(numpadTarget.cartId, val)
      closeModal()
    },
    // Variant picker
    variantPicker, pickerQty, setPickerQty, pickerNote, setPickerNote,
    pickerWeight, setPickerWeight,
    onVariantConfirm: (variantName) => addToCartDirect(variantPicker.product, pickerQty, variantName, pickerNote, pickerWeight ? Number(pickerWeight) : null),
    // Receipt
    receiptOrder, seller, sym,
    onReceiptClose: () => { setReceiptOrder(null); closeModal(); barcodeRef.current?.focus() },
    // Hold label
    onHoldConfirm: (label) => { holdOrder(label); closeModal() },
    // Gold rate
    goldRates, onGoldRateSave: async (rates) => {
      const today = new Date().toISOString().split('T')[0]
      await supabase.from('pos_gold_rates').upsert({ seller_id: seller.id, effective_date: today, ...rates, set_by: staffSession?.name || 'Seller' }, { onConflict: 'seller_id,effective_date' })
      await fetchGoldRates(); closeModal(); showToast('Gold rates updated')
    },
    // Custom item
    onCustomItemAdd: (name, price, qty) => {
      setCart(prev => [...prev, { cartId: 'custom-' + Date.now(), id: null, name, unit_price: Number(price), quantity: qty, stock: 999, note: 'Custom item', product_code: null, variant_name: null, is_service: false }])
      closeModal(); showToast(name + ' added')
    },
    // Void
    sellerId: seller.id, staffSession,
    onVoidComplete: () => { fetchTodayStats(); closeModal() },
    // Return/exchange
    onReturnComplete: () => { fetchProducts(); fetchTodayStats(); closeModal() },
    // Shift close
    todayStats, posMode, options,
    onShiftComplete: async (report) => {
      await supabase.from('pos_shift_reports').insert({ seller_id: seller.id, ...report })
      await fetchTodayStats(); closeModal(); showToast('Shift closed')
    },
  }

  // ── Main render ───────────────────────────────────────────────
  return (
    <div style={{ flex:1, overflowX:'hidden', background:'var(--bg-page)', fontFamily:'var(--font-ui)' }}>
      <Topbar title="Point of Sale" />

      {/* ── POS Header bar ── */}
      <div style={{ background:'var(--primary)', padding:'8px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          {/* Mode badge */}
          <div style={{ display:'flex', alignItems:'center', gap:'6px', padding:'4px 12px', borderRadius:'var(--radius-full)', background:'rgba(255,255,255,0.12)', fontSize:'11px', fontWeight:700, color:'#fff' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d={POS_MODES[posMode]?.icon || POS_MODES.standard.icon}/></svg>
            {POS_MODES[posMode]?.label || 'Standard'}
          </div>
          {/* Staff badge */}
          {staffSession && (
            <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11.5px', color:'rgba(255,255,255,0.8)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {staffSession.name} · {staffSession.role}
            </div>
          )}
          {/* Branch */}
          {options.branchName && (
            <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.7)' }}>
              📍 {options.branchName}
            </div>
          )}
        </div>

        {/* Today stats */}
        <div style={{ display:'flex', gap:'16px' }}>
          {[
            { label:'Sales',   value:`${sym}${todayStats.sales.toLocaleString()}`, hi:true },
            { label:'Orders',  value:todayStats.orders },
            { label:'Returns', value:todayStats.returns },
          ].map(s => (
            <div key={s.label} style={{ textAlign:'center' }}>
              <div style={{ fontSize:'14px', fontWeight:700, color:s.hi?'#fff':'rgba(255,255,255,0.9)' }}>{s.value}</div>
              <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.5)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Switch staff */}
        {seller?.pos_require_pin && (
          <button onClick={() => setShowStaffLogin(true)}
            style={{ padding:'5px 12px', borderRadius:'var(--radius-md)', border:'1px solid rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.1)', color:'#fff', fontSize:'11.5px', fontWeight:600, cursor:'pointer' }}>
            Switch staff
          </button>
        )}
      </div>

      {/* ── 3-panel layout ── */}
      <div className="pos-layout" style={{ display:'grid', gridTemplateColumns:'300px 1fr 280px', gap:0, height:'calc(100vh - 104px)', overflow:'hidden' }}>

        {/* LEFT — Cart */}
        <div style={{ borderRight:'1px solid var(--border)', overflowY:'auto', background:'var(--bg-card)' }}>
          <POSCart {...cartProps} />
        </div>

        {/* CENTRE — Products */}
        <div style={{ overflowY:'auto', background:'var(--bg-page)' }}>
          <POSProducts {...productProps} />
        </div>

        {/* RIGHT — Options */}
        <div style={{ borderLeft:'1px solid var(--border)', overflowY:'auto', background:'var(--bg-card)' }}>
          <POSOptions {...optionsProps} />
        </div>
      </div>

      {/* ── Modals ── */}
      <POSModals {...modalProps} />

      <style>{`
        @keyframes pos-scanline { 0%{top:20%} 50%{top:75%} 100%{top:20%} }
        @keyframes pos-fade { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
        .pos-modal-in { animation: pos-fade 0.18s ease-out both; }
        @media(max-width:1100px) {
          .pos-layout { grid-template-columns: 280px 1fr 0 !important; }
          .pos-options-panel { display: none !important; }
        }
        @media(max-width:768px) {
          .pos-layout { grid-template-columns: 1fr !important; height: auto !important; }
          .pos-cart-panel { display: none; }
          .pos-cart-drawer { display: flex !important; }
        }
      `}</style>
    </div>
  )
}

// ── Staff Login Screen ────────────────────────────────────────────
function POSStaffLogin({ sellerId, onLogin, onSkip }) {
  const [staff, setStaff]       = useState([])
  const [selected, setSelected] = useState(null)
  const [pin, setPin]           = useState('')
  const [error, setError]       = useState('')
  const [loading, setLoading]   = useState(false)
  const [fetching, setFetching] = useState(true)

  useEffect(() => {
    supabase.from('seller_staff').select('id, name, role, branch').eq('seller_id', sellerId).eq('is_active', true).order('name')
      .then(({ data }) => { setStaff(data || []); setFetching(false) })
  }, [sellerId])

  const handlePinDigit = (d) => { if (pin.length < 4) setPin(p => p + d) }
  const handleBackspace = () => setPin(p => p.slice(0, -1))

  const handleLogin = async () => {
    if (!selected || pin.length !== 4) return
    setLoading(true); setError('')
    const { data, error: fnErr } = await supabase.functions.invoke('verify-staff-pin', { body: { seller_id: sellerId, staff_id: selected.id, pin } })
    setLoading(false)
    if (fnErr || !data?.valid) { setError('Incorrect PIN — try again'); setPin(''); return }
    onLogin(data.staff)
  }

  return (
    <div style={{ flex:1, background:'var(--bg-page)', fontFamily:'var(--font-ui)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:'24px' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)', padding:'32px', width:'100%', maxWidth:'420px', boxShadow:'var(--shadow-md)', textAlign:'center' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin:'0 auto 16px', display:'block' }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        <div style={{ fontSize:'18px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px' }}>Staff Login</div>
        <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'24px' }}>Select your name and enter your PIN</div>

        {fetching ? (
          <div style={{ padding:'24px', color:'var(--text-muted)' }}>Loading staff...</div>
        ) : staff.length === 0 ? (
          <div style={{ padding:'16px', color:'var(--text-muted)', fontSize:'13px', marginBottom:'16px' }}>No staff configured yet. <button onClick={onSkip} style={{ color:'var(--primary)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Continue as owner →</button></div>
        ) : (
          <>
            {/* Staff selection */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', justifyContent:'center', marginBottom:'20px' }}>
              {staff.map(s => (
                <button key={s.id} onClick={() => { setSelected(s); setPin(''); setError('') }}
                  style={{ padding:'10px 16px', borderRadius:'var(--radius-md)', border:'2px solid', borderColor: selected?.id === s.id ? 'var(--primary)' : 'var(--border)', background: selected?.id === s.id ? 'var(--primary-light)' : 'var(--bg-card)', fontWeight:600, fontSize:'13px', color: selected?.id === s.id ? 'var(--primary)' : 'var(--text-primary)', cursor:'pointer' }}>
                  {s.name}
                  <div style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:400 }}>{s.role}</div>
                </button>
              ))}
            </div>

            {selected && (
              <>
                {/* PIN display */}
                <div style={{ display:'flex', justifyContent:'center', gap:'12px', marginBottom:'20px' }}>
                  {[0,1,2,3].map(i => (
                    <div key={i} style={{ width:'16px', height:'16px', borderRadius:'50%', background: pin.length > i ? 'var(--primary)' : 'var(--border)' }} />
                  ))}
                </div>

                {/* PIN pad */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'16px' }}>
                  {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((d, i) => (
                    <button key={i} onClick={() => typeof d === 'number' ? handlePinDigit(String(d)) : d === '⌫' ? handleBackspace() : null}
                      disabled={d === ''}
                      style={{ padding:'16px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background: d === '' ? 'transparent' : 'var(--bg-card)', fontSize:'18px', fontWeight:600, color:'var(--text-primary)', cursor: d === '' ? 'default' : 'pointer', opacity: d === '' ? 0 : 1 }}>
                      {d}
                    </button>
                  ))}
                </div>

                {error && <div style={{ color:'var(--error)', fontSize:'12.5px', marginBottom:'12px' }}>{error}</div>}

                <button onClick={handleLogin} disabled={pin.length !== 4 || loading}
                  style={{ width:'100%', padding:'13px', borderRadius:'var(--radius-lg)', background: pin.length === 4 ? 'var(--primary)' : 'var(--border)', border:'none', fontSize:'14px', fontWeight:700, color:'#fff', cursor: pin.length === 4 ? 'pointer' : 'not-allowed', marginBottom:'10px' }}>
                  {loading ? 'Verifying...' : 'Login'}
                </button>
              </>
            )}

            <button onClick={onSkip} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:'12px', cursor:'pointer', padding:'4px' }}>
              Skip — continue without login
            </button>
          </>
        )}
      </div>
    </div>
  )
}